import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  CheckCircle2,
  XCircle,
  MapPin,
  User,
  GraduationCap,
  CalendarDays,
  Banknote,
  Clock,
  ShieldCheck,
  ArrowRight,
  Lock,
  FileText,
  Tag,
  Home,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import CtaFinal from "components/cta/CtaFinal";
import { AppButton } from "components/misc/Buttons";
import useInView from "helpers/useInView";
import { fadeUpVariants, fadeInVariants } from "lib/animations";
import { attestationsService } from "services/attestations.service";
import type {
  VerifyAttestationResponse,
  VerifyBlockedAccountResponse,
  VerifyHousingResponse,
  AttestationStatus,
  BlockedAccountStatus,
} from "types/attestation.types";
import { IMAGES } from "constants/images";

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C_AVI = "#ff6c00";
const C_BA = "#1e5ac8";
const C_LOG = "#16a34a";
const C_DARK = "#111111";

// ─── Document-type helpers ─────────────────────────────────────────────────────

type DocType = "avi" | "ba" | "housing";
type VerifyResult =
  | VerifyAttestationResponse
  | VerifyBlockedAccountResponse
  | VerifyHousingResponse;

/**
 * Codes starting with "LOG" are Housing Attestation documents.
 * Codes starting with "BA" are Blocked Account documents.
 * Everything else is AVI.
 */
const detectDocType = (code: string): DocType => {
  const upper = code.trim().toUpperCase();
  if (upper.startsWith("LOG")) return "housing";
  if (upper.startsWith("BA")) return "ba";
  return "avi";
};

const docAccent = (t: DocType) =>
  t === "ba" ? C_BA : t === "housing" ? C_LOG : C_AVI;

// ─── Status helpers ────────────────────────────────────────────────────────────
// AVI statuses (English values) and BA statuses (French values) are unified here.

type AnyStatus = AttestationStatus | BlockedAccountStatus;

const STATUS_LABEL: Record<AnyStatus, string> = {
  // AVI — valeurs anglaises
  active: "Actif",
  pending: "En attente de traitement",
  revoked: "Révoqué",
  expired: "Expiré",
  cancelled: "Annulé",
  // Compte Bloqué — valeurs françaises
  actif: "Actif",
  en_attente: "En attente de traitement",
  révoqué: "Révoqué",
  expiré: "Expiré",
  annulé: "Annulé",
};

const STATUS_COLOR: Record<AnyStatus, string> = {
  // AVI
  active: "#22c55e",
  pending: "#f59e0b",
  revoked: "#ef4444",
  expired: "#6b7280",
  cancelled: "#ef4444",
  // Compte Bloqué
  actif: "#22c55e",
  en_attente: "#f59e0b",
  révoqué: "#ef4444",
  expiré: "#6b7280",
  annulé: "#ef4444",
};

// ─── Motion wrapper ────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Styled components ─────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 5rem 1.5rem 6rem;
`;

const SearchCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 2.5rem;
  margin-bottom: 3rem;
`;

const SearchEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${C_AVI};
  margin-bottom: 0.5rem;
`;

const SearchTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: ${C_DARK};
  margin-bottom: 0.5rem;
`;

const SearchSub = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const SearchRow = styled.form`
  display: flex;
  gap: 0;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 2.75rem;
  border: 1.5px solid #e5e7eb;
  border-right: none;
  background: #fafafa;
  font-size: 0.95rem;
  color: ${C_DARK};
  outline: none;
  box-sizing: border-box;
  letter-spacing: 0.08em;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: ${C_AVI};
    background: white;
  }
  &::placeholder {
    color: #c1c1c1;
    letter-spacing: 0.04em;
  }

  @media (max-width: 640px) {
    border-right: 1.5px solid #e5e7eb;
  }
`;

const SearchHint = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StatePanel = styled.div`
  margin-bottom: 2rem;
`;

const ErrorPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
  background: #fef2f2;
  border-left: 3px solid #ef4444;
`;

const ErrorTitle = styled.p`
  font-size: 0.85rem;
  font-weight: 900;
  color: #ef4444;
  margin-bottom: 0.25rem;
`;

const ErrorDesc = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
`;

const ResultCard = styled.div<{ $accent: string }>`
  background: white;
  border: 1px solid #e5e7eb;
  border-top: 3px solid ${(p) => p.$accent};
  overflow: hidden;
`;

const ResultHeader = styled.div`
  padding: 1.75rem 2rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ResultHeaderLeft = styled.div``;

const DocTypePill = styled.div<{ $accent: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.7rem;
  font-size: 0.6rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${(p) => p.$accent};
  background: ${(p) => p.$accent}18;
  border: 1px solid ${(p) => p.$accent}40;
  margin-bottom: 0.5rem;
`;

const ResultRef = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #9ca3af;
  margin-bottom: 0.35rem;
`;

const ResultName = styled.h3`
  font-size: 1.25rem;
  font-weight: 900;
  color: ${C_DARK};
`;

const StatusBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 1rem;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${(p) => p.$color};
  background: ${(p) => p.$color}18;
  border-left: 3px solid ${(p) => p.$color};
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ResultField = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 2rem;
  border-bottom: 1px solid #f5f5f5;
  border-right: 1px solid #f5f5f5;
  &:nth-child(2n) {
    border-right: none;
  }
  &:nth-last-child(-n + 2) {
    border-bottom: none;
  }
  @media (max-width: 640px) {
    border-right: none;
    &:nth-last-child(-n + 2) {
      border-bottom: 1px solid #f5f5f5;
    }
    &:last-child {
      border-bottom: none;
    }
  }
`;

const FieldIcon = styled.div<{ $accent: string }>`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$accent}14;
  color: ${(p) => p.$accent};
  flex-shrink: 0;
`;

const FieldContent = styled.div``;

const FieldLabel = styled.p`
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3af;
  margin-bottom: 0.25rem;
`;

const FieldValue = styled.p`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${C_DARK};
`;

const ResultFooter = styled.div<{ $accent: string }>`
  padding: 1.25rem 2rem;
  background: ${(p) => p.$accent}08;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-top: 1px solid ${(p) => p.$accent}20;
`;

const FooterVerified = styled.p<{ $accent: string }>`
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(p) => p.$accent};
`;

const DisbursementNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0 2rem 0;
  padding: 0.9rem 1.2rem;
  background: #eef2ff;
  border-left: 3px solid ${C_BA};
  font-size: 0.78rem;
  color: #1e3a8a;
  font-weight: 600;
`;

const HowSection = styled.div`
  margin-top: 4rem;
  border-top: 1px solid #f0f0f0;
  padding-top: 3rem;
`;

const HowTitle = styled.h3`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #9ca3af;
  margin-bottom: 2rem;
`;

const HowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border: 1px solid #e5e7eb;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const HowItem = styled.div`
  padding: 1.75rem 1.5rem;
  border-right: 1px solid #e5e7eb;
  &:last-child {
    border-right: none;
  }
  @media (max-width: 640px) {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    &:last-child {
      border-bottom: none;
    }
  }
`;

const HowNum = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: ${C_AVI};
  margin-bottom: 0.75rem;
`;

const HowItemTitle = styled.p`
  font-size: 0.82rem;
  font-weight: 900;
  color: ${C_DARK};
  margin-bottom: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const HowItemDesc = styled.p`
  font-size: 0.78rem;
  color: #9ca3af;
  line-height: 1.6;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

type VerifyState = "idle" | "loading" | "success" | "error";

const VerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get("code") ?? "");
  const [state, setState] = useState<VerifyState>("idle");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [docType, setDocType] = useState<DocType>("avi");
  const [errMsg, setErrMsg] = useState("");

  const runVerification = async (rawCode: string) => {
    const trimmed = rawCode.trim().toUpperCase();
    if (!trimmed) return;
    const detected = detectDocType(trimmed);

    setState("loading");
    setResult(null);
    setDocType(detected);
    setErrMsg("");

    try {
      if (detected === "ba") {
        setResult(await attestationsService.verifyBlockedAccount(trimmed));
      } else if (detected === "housing") {
        setResult(await attestationsService.verifyHousing(trimmed));
      } else {
        setResult(await attestationsService.verifyAttestation(trimmed));
      }
      setState("success");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      setErrMsg(
        status === 404
          ? "Aucun document trouvé avec ce code. Vérifiez la saisie."
          : "Une erreur est survenue. Réessayez dans quelques instants.",
      );
      setState("error");
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    await runVerification(code);
  };

  // Scan d'un QR code : le code arrive via ?code=, on lance la vérification
  // automatiquement sans attendre un clic sur "Vérifier".
  useEffect(() => {
    const fromQr = searchParams.get("code");
    if (fromQr?.trim()) {
      runVerification(fromQr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

  const accent = docAccent(docType);
  const isBA = (r: VerifyResult): r is VerifyBlockedAccountResponse =>
    "reason" in r;
  const isHousing = (r: VerifyResult): r is VerifyHousingResponse =>
    "housingAddress" in r;

  return (
    <LandingLayout disableAnimation>
      <Hero
        backgroundImageSrc={IMAGES.studentImg}
        notification="Authentification de documents"
        heading={
          <>
            <span>Vérifiez l'authenticité</span>
            <br />
            <SlantedBackground>d'un document.</SlantedBackground>
          </>
        }
        description="Entrez le code imprimé sur votre document (AVI, Compte Bloqué ou Attestation de Logement) pour confirmer son authenticité et consulter les informations associées."
      />

      <PageWrapper>
        {/* ── Search card ── */}
        <FadeUpSection>
          <SearchCard>
            <SearchEyebrow>Vérification en ligne</SearchEyebrow>
            <SearchTitle>Entrez le code du document</SearchTitle>
            <SearchSub>
              Le code se trouve sur votre document, sous la mention{" "}
              <strong>« Code de vérification »</strong>. Les codes AVI sont
              composés de chiffres (ex.&nbsp;: 032685042). Les codes Compte
              Bloqué commencent par <strong>BA</strong> (ex.&nbsp;:
              BA120726001), et les codes Attestation de Logement par{" "}
              <strong>LOG</strong> (ex.&nbsp;: LOG040826017).
            </SearchSub>

            <SearchRow onSubmit={handleSearch}>
              <SearchInputWrapper>
                <SearchIconWrap>
                  <Search size={16} />
                </SearchIconWrap>
                <SearchInput
                  type="text"
                  placeholder="032685042, BA120726001 ou LOG040826017"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                />
              </SearchInputWrapper>
              <AppButton
                type="submit"
                size="lg"
                loading={state === "loading"}
                disabled={state === "loading" || !code.trim()}
                iconRight={<ArrowRight size={15} />}
                style={{ flexShrink: 0 }}
              >
                {state === "loading" ? "Vérification..." : "Vérifier"}
              </AppButton>
            </SearchRow>

            <SearchHint>
              <ShieldCheck size={12} color={C_AVI} />
              La vérification est gratuite et accessible à tous.
            </SearchHint>
          </SearchCard>
        </FadeUpSection>

        {/* ── Result / error panel ── */}
        <AnimatePresence mode="wait">
          {state === "error" && (
            <MDiv
              key="error"
              variants={fadeInVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <StatePanel>
                <ErrorPanel>
                  <XCircle
                    size={22}
                    color="#ef4444"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <div>
                    <ErrorTitle>Document introuvable</ErrorTitle>
                    <ErrorDesc>{errMsg}</ErrorDesc>
                  </div>
                </ErrorPanel>
              </StatePanel>
            </MDiv>
          )}

          {state === "success" && result && (
            <MDiv
              key="result"
              variants={fadeInVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <StatePanel>
                <ResultCard $accent={accent}>
                  {/* ── Header ── */}
                  <ResultHeader>
                    <ResultHeaderLeft>
                      <DocTypePill $accent={accent}>
                        {docType === "ba" ? (
                          <>
                            <Lock size={9} />
                            &nbsp;Compte Bloqué
                          </>
                        ) : docType === "housing" ? (
                          <>
                            <Home size={9} />
                            &nbsp;Attestation de Logement
                          </>
                        ) : (
                          <>
                            <FileText size={9} />
                            &nbsp;Attestation AVI
                          </>
                        )}
                      </DocTypePill>
                      <ResultRef>Référence · {result.number}</ResultRef>
                      <ResultName>{result.student}</ResultName>
                    </ResultHeaderLeft>
                    <StatusBadge
                      $color={
                        STATUS_COLOR[result.status as AnyStatus] ?? "#9ca3af"
                      }
                    >
                      <span
                        style={{
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background:
                            STATUS_COLOR[result.status as AnyStatus] ??
                            "#9ca3af",
                          display: "inline-block",
                        }}
                      />
                      {STATUS_LABEL[result.status as AnyStatus] ??
                        result.status}
                    </StatusBadge>
                  </ResultHeader>

                  {/* ── One-time disbursement notice (BA only) ── */}
                  {docType === "ba" && isBA(result) && (
                    <DisbursementNotice>
                      <Lock size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        Versement unique · Motif :{" "}
                        <strong>{result.reasonLabel}</strong>. Cette somme est
                        débloquée en une seule fois à l'arrivée en France.
                      </span>
                    </DisbursementNotice>
                  )}

                  {/* ── Fields grid ── */}
                  <ResultGrid>
                    <ResultField>
                      <FieldIcon $accent={accent}>
                        <User size={15} />
                      </FieldIcon>
                      <FieldContent>
                        <FieldLabel>Nom de l'étudiant</FieldLabel>
                        <FieldValue>{result.student}</FieldValue>
                      </FieldContent>
                    </ResultField>

                    {/* École/Université (AVI, BA) | Adresse du logement (Housing) */}
                    {docType === "housing" && isHousing(result) ? (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <Home size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>Adresse du logement</FieldLabel>
                          <FieldValue>{result.housingAddress}</FieldValue>
                        </FieldContent>
                      </ResultField>
                    ) : (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <GraduationCap size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>École / Université</FieldLabel>
                          <FieldValue>
                            {("schoolName" in result && result.schoolName) ??
                              "—"}
                          </FieldValue>
                        </FieldContent>
                      </ResultField>
                    )}

                    {/* AVI → année académique | BA → motif | Housing → date d'arrivée */}
                    {docType === "avi" ? (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <CalendarDays size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>Année académique</FieldLabel>
                          <FieldValue>
                            {("academicYear" in result &&
                              result.academicYear) ??
                              "—"}
                          </FieldValue>
                        </FieldContent>
                      </ResultField>
                    ) : docType === "housing" && isHousing(result) ? (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <CalendarDays size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>Date d'arrivée prévue</FieldLabel>
                          <FieldValue>
                            {formatDate(result.arrivalDate)}
                          </FieldValue>
                        </FieldContent>
                      </ResultField>
                    ) : (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <Tag size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>Motif</FieldLabel>
                          <FieldValue>
                            {isBA(result) ? result.reasonLabel : "—"}
                          </FieldValue>
                        </FieldContent>
                      </ResultField>
                    )}

                    <ResultField>
                      <FieldIcon $accent={accent}>
                        <Banknote size={15} />
                      </FieldIcon>
                      <FieldContent>
                        <FieldLabel>
                          {docType === "ba"
                            ? "Montant bloqué"
                            : docType === "housing"
                              ? "Loyer mensuel"
                              : "Montant certifié"}
                        </FieldLabel>
                        <FieldValue>
                          {parseFloat(result.amount).toLocaleString("fr-FR")}{" "}
                          {result.currency}
                        </FieldValue>
                      </FieldContent>
                    </ResultField>

                    <ResultField>
                      <FieldIcon $accent={accent}>
                        <Clock size={15} />
                      </FieldIcon>
                      <FieldContent>
                        <FieldLabel>Date d'émission</FieldLabel>
                        <FieldValue>{formatDate(result.createdAt)}</FieldValue>
                      </FieldContent>
                    </ResultField>

                    {docType === "housing" && isHousing(result) && (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <Clock size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>Durée de location</FieldLabel>
                          <FieldValue>
                            {result.rentalDurationMonths} mois
                          </FieldValue>
                        </FieldContent>
                      </ResultField>
                    )}

                    {result.studentCountry && (
                      <ResultField>
                        <FieldIcon $accent={accent}>
                          <MapPin size={15} />
                        </FieldIcon>
                        <FieldContent>
                          <FieldLabel>Pays</FieldLabel>
                          <FieldValue>{result.studentCountry}</FieldValue>
                        </FieldContent>
                      </ResultField>
                    )}
                  </ResultGrid>

                  {/* ── Footer ── */}
                  <ResultFooter $accent={accent}>
                    <CheckCircle2 size={16} color={accent} />
                    <FooterVerified $accent={accent}>
                      {docType === "ba"
                        ? "Document authentique — Compte Bloqué · Vérifié par MomsEduc Study"
                        : docType === "housing"
                          ? "Document authentique — Attestation de Logement · Vérifié par MomsEduc Study"
                          : "Document authentique — Attestation AVI · Vérifié par MomsEduc Study"}
                    </FooterVerified>
                  </ResultFooter>
                </ResultCard>
              </StatePanel>
            </MDiv>
          )}
        </AnimatePresence>

        {/* ── How it works ── */}
        <FadeUpSection delay={0.15}>
          <HowSection>
            <HowTitle>Comment vérifier un document ?</HowTitle>
            <HowGrid>
              {[
                {
                  num: "01",
                  title: "Localisez le code",
                  desc: "Le code de vérification est imprimé sur le document sous la mention « Code de vérification ».",
                },
                {
                  num: "02",
                  title: "Saisissez le code",
                  desc: "AVI : chiffres uniquement · Compte Bloqué : commence par BA · Attestation de Logement : commence par LOG. Cliquez ensuite sur « Vérifier ».",
                },
                {
                  num: "03",
                  title: "Consultez le résultat",
                  desc: "Les informations de l'étudiant, le type de document (AVI ou Compte Bloqué) et son statut s'affichent instantanément.",
                },
              ].map((s) => (
                <HowItem key={s.num}>
                  <HowNum>{s.num}</HowNum>
                  <HowItemTitle>{s.title}</HowItemTitle>
                  <HowItemDesc>{s.desc}</HowItemDesc>
                </HowItem>
              ))}
            </HowGrid>
          </HowSection>
        </FadeUpSection>
      </PageWrapper>

      <CtaFinal
        label="Besoin d'un document ?"
        title="AVI ou Compte Bloqué en 48h"
        description="Nous délivrons des AVI et des attestations de compte bloqué certifiées, reconnues par les consulats et Campus France."
      />
    </LandingLayout>
  );
};

export default VerifyPage;
